"use client";

import { useMemo } from "react";
import { ItemCategory, ItemImage, CategorySelection } from "@/lib/types";
import { categoryLabels } from "@/lib/categoryLabels";
import type { CategoryStructure } from "@/lib/categoryStructure";
import { CategoryDropdown } from "@/components/CategoryDropdown";
import { CategoryAutoDetector } from "@/components/CategoryAutoDetector";
import BrandSelector from "@/components/listing/BrandSelector";
import SizeInput from "@/components/listing/SizeInput";
import ModelInput from "@/components/listing/ModelInput";
import PriceInput from "@/components/listing/PriceInput";
import ItemImageManager from "@/components/ItemImageManager";
import Input from "@/components/ui/Input";
import FieldError from "@/components/ui/FieldError";
import { getCategoryFieldConfig } from "@/lib/listing/fieldConfigs";
import { sellListingShowsModelField } from "@/lib/listing/listingIdentityPolicy";
import { getStepTwoSizePresentation } from "@/lib/listing/listingSpecSellPresentation";
import EnhancedTitleInput from "@/components/listing/EnhancedTitleInput";
import ListingFamilySpecFieldsHost from "@/components/listing/ListingFamilySpecFieldsHost";
import { getFamilyRuntime } from "@/lib/listing/listingFamilyRuntime";
import {
  listingSpecPluginHidesBrandLine,
  listingSpecPluginHidesModelLine,
  listingSpecPluginHidesSizeLine,
} from "@/lib/listing/listingSpecPlugin";
import DescriptionContactPolicyNotice from "@/components/listing/DescriptionContactPolicyNotice";
import {
  LISTING_DEFECTS_PLACEHOLDER,
  LISTING_DESCRIPTION_PLACEHOLDER,
  LISTING_TITLE_POLICY_HINT,
} from "@/lib/listing/listingFreeTextPolicy";
import SubcategoryRulesCard from "@/components/listing/SubcategoryRulesCard";
import SellCategoryAvailabilityNotice from "@/components/listing/SellCategoryAvailabilityNotice";
import ChairSofaCategoryNudge from "@/components/listing/ChairSofaCategoryNudge";
import type { SubcategoryListingRule } from "@/lib/subcategoryRules";
import type { BulkListingItemDraft } from "@/lib/listing/bulkListingTypes";
import type { SellListingFormErrors } from "@/lib/listing/sellListingValidation";
import type { SellPriceBandSnapshot } from "@/lib/hooks/useSellPriceBand";

export interface BulkItemFormSectionsProps {
  draft: BulkListingItemDraft;
  categoryStructure: CategoryStructure[];
  subcategoryRule: SubcategoryListingRule | null;
  errors: Pick<
    SellListingFormErrors,
    | "title"
    | "price"
    | "category"
    | "images"
    | "description"
    | "defects"
    | "brand"
  >;
  specError?: string;
  sellerCategoryDetectionEnabled: boolean;
  onCategorySelect: (selection: CategorySelection) => void;
  onFieldChange: (name: string, value: unknown) => void;
  /** Free-text fields (description/defects): validate on blur, not as-you-type. */
  onFieldBlur?: (name: string, value: unknown) => void;
  onImagesChange: (images: ItemImage[]) => void;
  onRulesAcknowledgedChange: (value: boolean) => void;
  bandTitle?: string;
  listingAttributes?: Record<string, unknown> | null;
  priceBandEnforcementActive?: boolean;
  sellPriceBand?: SellPriceBandSnapshot | null;
  sellPriceBandLoading?: boolean;
  sellPriceBandError?: string | null;
  bandRecommendationChipsEnabled?: boolean;
}

export default function BulkItemFormSections({
  draft,
  categoryStructure,
  subcategoryRule,
  errors,
  specError,
  sellerCategoryDetectionEnabled,
  onCategorySelect,
  onFieldChange,
  onFieldBlur,
  onImagesChange,
  onRulesAcknowledgedChange,
  bandTitle,
  listingAttributes,
  priceBandEnforcementActive = false,
  sellPriceBand = null,
  sellPriceBandLoading = false,
  sellPriceBandError = null,
  bandRecommendationChipsEnabled = false,
}: BulkItemFormSectionsProps) {
  const showPreSelectionNotices = !draft.subcategory;

  const config = getCategoryFieldConfig(draft.category);
  const showSize =
    Boolean(config?.size.enabled) &&
    !listingSpecPluginHidesSizeLine(draft.subcategory);
  const showModel =
    sellListingShowsModelField(Boolean(config?.model)) &&
    !listingSpecPluginHidesModelLine(draft.subcategory);
  const showStructuredSpec = getFamilyRuntime(draft.subcategory) != null;

  const bulkSizePresentation = useMemo(() => {
    if (!config?.size.enabled || !draft.category) return null;
    const activeSet = new Set(
      categoryStructure.flatMap((c) => c.subcategories.map((s) => s.id)),
    );
    return getStepTwoSizePresentation({
      category: draft.category,
      subcategoryId: draft.subcategory,
      activeSubcategoryIds: activeSet,
      fallback: config.size,
    });
  }, [draft.category, draft.subcategory, categoryStructure, config]);

  return (
    <div className="space-y-10">
      <section className="space-y-4">
        <h3 className="text-base font-semibold text-foreground">
          Category &amp; brand
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700/50 dark:bg-amber-950/40">
              <p className="text-sm text-amber-900 dark:text-amber-100">
                <span className="font-semibold">Please note:</span> Items
                intentionally listed under the wrong category will be deleted
                without question.
              </p>
            </div>
            {showPreSelectionNotices ? (
              <SellCategoryAvailabilityNotice
                categoryStructure={categoryStructure}
              />
            ) : null}
            <label className="block mt-2 mb-2 text-sm font-medium text-foreground">
              Category <span className="text-red-500">*</span>
            </label>
            <CategoryDropdown
              onSelect={onCategorySelect}
              name="category"
              placeholder="Select a category"
              className="w-full"
              categoryStructure={categoryStructure}
              initialValue={
                draft.category && draft.subcategory
                  ? {
                      categoryId: draft.category,
                      subcategoryId: draft.subcategory,
                      categoryName:
                        categoryLabels[draft.category] || draft.category,
                      subcategoryName:
                        categoryStructure
                          .find((cat) => cat.id === draft.category)
                          ?.subcategories.find(
                            (sub) => sub.id === draft.subcategory,
                          )?.name || "",
                    }
                  : undefined
              }
            />
            <FieldError message={errors.category} />
            {draft.subcategory && subcategoryRule && (
              <SubcategoryRulesCard
                subcategoryName={
                  categoryStructure
                    .find((c) => c.id === draft.category)
                    ?.subcategories.find((s) => s.id === draft.subcategory)
                    ?.name ?? draft.subcategory
                }
                rule={subcategoryRule}
                acknowledged={draft.rulesAcknowledged}
                onAcknowledgedChange={onRulesAcknowledgedChange}
              />
            )}
            {sellerCategoryDetectionEnabled &&
              draft.title &&
              !draft.category && (
                <div className="mt-2">
                  <CategoryAutoDetector
                    title={draft.title}
                    onDetect={(category, subcategory) => {
                      if (category) {
                        const categoryData = categoryStructure.find(
                          (cat) => cat.id === category,
                        );
                        if (categoryData) {
                          const subcategoryData =
                            categoryData?.subcategories.find(
                              (sub) => sub.id === subcategory,
                            );
                          if (subcategoryData) {
                            onCategorySelect({
                              categoryId: category,
                              subcategoryId: subcategory,
                              categoryName:
                                categoryLabels[category as ItemCategory] ||
                                category,
                              subcategoryName: subcategoryData.name,
                            });
                          }
                        }
                      }
                    }}
                    minWords={3}
                    debounceMs={1000}
                    showLoadingIndicator={true}
                  />
                </div>
              )}
          </div>
          {draft.category &&
            getCategoryFieldConfig(draft.category)?.brand &&
            !listingSpecPluginHidesBrandLine(draft.subcategory) && (
              <div>
                <label className="block mb-2 text-sm font-medium text-foreground">
                  Brand{" "}
                  <span className="text-text-light text-xs">(optional)</span>
                </label>
                <BrandSelector
                  value={draft.brand}
                  onChange={(brand) => onFieldChange("brand", brand)}
                  category={draft.category}
                  placeholder="Search and choose a brand"
                  className="w-full"
                />
                <FieldError message={errors.brand} />
                <p className="mt-1 text-xs text-text-light">
                  Optional — skip if you don&apos;t see your brand
                </p>
              </div>
            )}
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold text-foreground">
          Size &amp; details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ListingFamilySpecFieldsHost
            subcategoryId={draft.subcategory}
            familySpecs={draft.familySpecs}
            onChange={(familySpecs) =>
              onFieldChange("familySpecs", familySpecs)
            }
            error={specError}
          />
          {showSize && config && (
            <div>
              <label className="block mb-2 text-sm font-medium text-foreground">
                {bulkSizePresentation?.fieldLabel ??
                  config.size.label ??
                  "Size"}{" "}
                {bulkSizePresentation?.showRequiredMarker ? (
                  <span className="text-red-500">*</span>
                ) : (
                  <span className="text-text-light text-xs">(optional)</span>
                )}
              </label>
              <SizeInput
                value={draft.size}
                onChange={(size) => onFieldChange("size", size)}
                config={bulkSizePresentation?.sizeInputConfig ?? config.size}
                helperTextOverride={
                  bulkSizePresentation?.helperTextOverride ?? null
                }
                className="w-full"
              />
              <FieldError message={specError} />
            </div>
          )}
          {showModel && (
            <div>
              <label className="block mb-2 text-sm font-medium text-foreground">
                Model{" "}
                <span className="text-text-light text-xs">(optional)</span>
              </label>
              <ModelInput
                value={draft.model}
                onChange={(model) => onFieldChange("model", model)}
                brand={draft.brand || null}
                subcategory={draft.subcategory}
                placeholder="Enter model name (optional)"
                className="w-full"
              />
            </div>
          )}
          {!showSize && !showModel && !showStructuredSpec && (
            <div className="col-span-2 text-sm text-text-light">
              No additional details required for this category.
            </div>
          )}
          {!showSize && !showStructuredSpec && specError && (
            <div className="col-span-2">
              <FieldError message={specError} />
            </div>
          )}
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold text-foreground">Title</h3>
        <div>
          <label className="block mb-2 text-sm font-medium text-foreground">
            Item Title <span className="text-red-500">*</span>
          </label>
          <EnhancedTitleInput
            brand={draft.brand}
            size={draft.size}
            model={draft.model}
            subcategoryId={draft.subcategory}
            category={draft.category}
            familySpecs={draft.familySpecs}
            title={draft.title}
            onTitleChange={(title) => onFieldChange("title", title)}
            error={errors.title}
            placeholder="What should we call this item?"
          />
          <FieldError message={errors.title} />
          <ChairSofaCategoryNudge
            title={draft.title}
            subcategoryId={draft.subcategory}
            categoryStructure={categoryStructure}
            onSwitchToSofa={onCategorySelect}
          />
          <p className="mt-2 text-xs text-text-light">
            {LISTING_TITLE_POLICY_HINT}
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold text-foreground">
          Price &amp; quantity
        </h3>
        <PriceInput
          stackedLayout
          value={draft.price}
          onChange={(e) => onFieldChange("price", e.target.value)}
          error={errors.price}
          itemTitle={bandTitle ?? draft.title}
          itemCategory={draft.category}
          itemType={draft.subcategory || ""}
          onSuggestedPrice={(price) => {
            onFieldChange("price", price.toLocaleString("en-NG"));
          }}
          onClearPrice={() => onFieldChange("price", "")}
          priceBandEnforcementActive={priceBandEnforcementActive}
          sellPriceBand={sellPriceBand}
          sellPriceBandLoading={sellPriceBandLoading}
          sellPriceBandError={sellPriceBandError}
          listingAttributes={listingAttributes}
          bandRecommendationChipsEnabled={bandRecommendationChipsEnabled}
          secondaryField={
            <div>
              <label className="block mb-2 text-sm font-medium text-foreground">
                Quantity
              </label>
              <Input
                name="quantity"
                type="number"
                min="1"
                value={draft.quantity}
                onChange={(e) => {
                  const value = e.target.value;
                  const numValue =
                    value === "" ? 1 : Math.max(1, parseInt(value, 10) || 1);
                  onFieldChange("quantity", numValue);
                }}
                className="w-full"
              />
            </div>
          }
        />
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold text-foreground">Images</h3>
        <div>
          {/* <label className="block mb-2 text-sm font-medium text-foreground">
 Item Images <span className="text-red-500">*</span>
 </label>
 <p className="text-sm text-text-light mb-4">
 Upload exactly 3 sharp, well-lit photos. The star marks your main
 image — it appears first in search.
 </p> */}
          <ItemImageManager
            images={draft.images}
            onChange={onImagesChange}
            maxImages={3}
            minImages={3}
            forcePermission={true}
          />
          <FieldError message={errors.images} />
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold text-foreground">
          Description &amp; defects
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block mb-2 text-sm font-medium text-foreground">
              Description{" "}
              <span className="text-text-light text-xs">(optional)</span>
            </label>
            <textarea
              name="description"
              value={draft.description}
              onChange={(e) => onFieldChange("description", e.target.value)}
              onBlur={(e) => onFieldBlur?.("description", e.target.value)}
              placeholder={LISTING_DESCRIPTION_PLACEHOLDER}
              className="w-full p-2 border rounded-lg min-h-[120px]"
              rows={4}
            />
            <FieldError message={errors.description} />
          </div>
          <div>
            <label className="block mb-2 text-sm font-medium text-foreground">
              Defects{" "}
              <span className="text-text-light text-xs">(optional)</span>
            </label>
            <textarea
              name="defects"
              value={draft.defects}
              onChange={(e) => onFieldChange("defects", e.target.value)}
              onBlur={(e) => onFieldBlur?.("defects", e.target.value)}
              placeholder={LISTING_DEFECTS_PLACEHOLDER}
              className="w-full p-2 border rounded-lg min-h-[120px]"
              rows={4}
            />
            <FieldError message={errors.defects} />
          </div>
        </div>
        <DescriptionContactPolicyNotice />
      </section>
    </div>
  );
}
